
import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FaEnvelope, FaExclamationCircle, FaCheck } from "react-icons/fa";
import { toast } from 'sonner';

const VerifyEmail = () => {
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { currentUser, logout, resendVerificationEmail, isEmailVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log("VerifyEmail: Current user state:", { currentUser, isEmailVerified });
    
    // If no user is logged in, redirect to sign in
    if (!currentUser) {
      console.log("No current user, redirecting to signin");
      navigate('/auth/signin');
      return;
    }

    // If email is already verified, redirect to dashboard
    if (isEmailVerified) {
      console.log("Email already verified, redirecting to dashboard");
      toast.success('Email verified successfully!');
      navigate('/dashboard');
    }
    
    // Check verification status every 5 seconds
    const checkVerificationInterval = setInterval(async () => {
      if (currentUser) {
        console.log("Checking email verification status...");
        await currentUser.reload();
        if (currentUser.emailVerified) {
          console.log("Email now verified, reloading page");
          clearInterval(checkVerificationInterval);
          window.location.reload(); // Force reload to update auth state
        }
      }
    }, 5000);

    return () => clearInterval(checkVerificationInterval);
  }, [currentUser, isEmailVerified, navigate]);

  // Function to handle resending verification email
  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      await resendVerificationEmail();
      toast.success("Verification email sent! Please check your inbox.");
      
      // Set a cooldown timer for resend button (60 seconds)
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(prevCooldown => {
          if (prevCooldown <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prevCooldown - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error("Error resending verification email:", error);
      toast.error("Failed to resend verification email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/auth/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 sm:px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Email Verification</h1>
          <p className="mt-2 text-gray-400">Verify your email to continue</p>
        </div>
        
        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Verify Your Email</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              We've sent a verification link to your email address
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center">
              <div className="rounded-full bg-truth/10 p-6">
                <FaEnvelope className="h-12 w-12 text-truth" />
              </div>
            </div>
            
            <div className="text-center">
              <p className="mb-2 text-sm sm:text-base">
                A verification email has been sent to:
              </p>
              <p className="font-semibold text-truth text-base sm:text-lg">
                {currentUser?.email}
              </p>
            </div>
            
            <div className="bg-blue-900/20 border border-blue-800 rounded-md p-4 text-sm">
              <div className="flex items-start gap-2">
                <FaExclamationCircle className="text-blue-400 mt-1" />
                <div>
                  <p>Please check your inbox and click the verification link to activate your account.</p>
                  <p className="mt-2">If you don't see the email, check your spam folder.</p>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              onClick={handleResendEmail} 
              disabled={loading || resendCooldown > 0}
              variant="outline" 
              className="w-full"
            >
              {resendCooldown > 0 
                ? `Resend email (${resendCooldown}s)` 
                : loading 
                  ? "Sending..." 
                  : "Resend verification email"}
            </Button>
            
            <div className="text-center">
              <p className="text-sm text-gray-400">
                Already verified?{" "}
                <Link to="/auth/signin" className="text-truth hover:underline">
                  Refresh login status
                </Link>
              </p>
            </div>
            
            <Button 
              variant="ghost" 
              className="text-gray-400 hover:text-white"
              onClick={handleSignOut}
            >
              Sign out
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
